// QuickLogger — T-016 Part A / T-035 B4 / T-095 §2 / T-098. The rep
// field is a button that opens a 64 px keypad sheet, ±1 stay for
// fine adjustment, and the big number updates live as the person
// types. The keypad is the only direct path to a number (voice
// control was removed in T-098). Set-done is disabled while
// reps === 0 in every entry path (button, keypad ✓, −1 clamp).

import { useState } from "react";
import type { JSX } from "react";
import { useI18n } from "../../i18n";
import { tick as audioTick } from "../../services/audioCues";
import { RepKeypad } from "./RepKeypad";

export interface QuickLoggerProps {
  /** The rep target shown as context (e.g. "Target: 3 × 10 · Beginner"). */
  targetReps: number;
  /** Sets the user has already completed; used to derive the target. */
  completedSets: number;
  /** Total sets; used to derive the target. */
  totalSets: number;
  /** Current rep count (controlled). */
  reps: number;
  /** Called when the rep count changes. */
  onRepsChange: (reps: number) => void;
  /** Called when the user confirms the set. */
  onDone: () => void;
  /** ADR-0013 D4: "Set done" is disabled at 0 reps. Defaults to true
   *  for backward compat with tests that pre-date the rule. */
  canConfirm?: boolean;
  /** Localized tier label for the target line (`Beginner` /
   *  `Intermediate` / `Mastery`). */
  tierLabel?: string;
}

export function QuickLogger({
  targetReps,
  completedSets,
  totalSets,
  reps,
  onRepsChange,
  onDone,
  canConfirm = true,
  tierLabel,
}: QuickLoggerProps): JSX.Element {
  const { t } = useI18n();
  const [keypadOpen, setKeypadOpen] = useState(false);

  const dec = () => {
    if (reps <= 0) return; // clamp at 0; no state update, no tick
    audioTick();
    onRepsChange(reps - 1);
  };
  const inc = () => {
    audioTick();
    onRepsChange(reps + 1);
  };

  return (
    <div
      data-testid="quick-logger"
      className="flex w-full flex-col items-stretch gap-6"
    >
      <p
        className="text-center font-subhead text-sm uppercase tracking-[0.06em] text-[var(--color-ink)]"
        data-testid="target-reps"
      >
        {t("workoutRunner.target", {
          sets: totalSets,
          reps: targetReps,
          tier: tierLabel ?? "",
        })}
      </p>
      <div className="flex items-center justify-center gap-3">
        <button
          type="button"
          onClick={dec}
          aria-label={t("workoutRunner.repMinus")}
          data-testid="rep-dec"
          className="border-2 border-[var(--color-line-strong)] bg-[var(--color-slab)] font-display text-[var(--color-ink-hi)] hover:border-[var(--color-lime)] hover:bg-[var(--color-slab-2)] active:scale-95 transition-all"
          style={{
            minHeight: "64px",
            minWidth: "64px",
            borderRadius: "var(--radius-sharp)",
            fontSize: 36,
          }}
        >
          −
        </button>
        <button
          type="button"
          data-testid="rep-count"
          aria-label={t("workoutRunner.repsOpen", { reps })}
          onClick={() => setKeypadOpen(true)}
          className="font-numeric tabular text-[var(--color-ink-hi)] transition-colors hover:text-[var(--color-lime)]"
          style={{
            fontSize: 96,
            lineHeight: 1,
            fontWeight: 700,
            minWidth: "4ch",
            textAlign: "center",
            padding: "4px 12px",
            borderRadius: "var(--radius-sharp)",
          }}
        >
          {reps}
        </button>
        <button
          type="button"
          onClick={inc}
          aria-label={t("workoutRunner.repPlus")}
          data-testid="rep-inc"
          className="border-2 border-[var(--color-line-strong)] bg-[var(--color-slab)] font-display text-[var(--color-ink-hi)] hover:border-[var(--color-lime)] hover:bg-[var(--color-slab-2)] active:scale-95 transition-all"
          style={{
            minHeight: "64px",
            minWidth: "64px",
            borderRadius: "var(--radius-sharp)",
            fontSize: 36,
          }}
        >
          +
        </button>
      </div>
      <p className="text-center font-sans text-sm text-[var(--color-ink)]">
        {t("workoutRunner.repsLabel")}
      </p>
      <button
        type="button"
        onClick={onDone}
        disabled={!canConfirm}
        data-testid="rep-done"
        className="font-display uppercase tracking-[0.02em] text-[var(--color-void)] disabled:cursor-not-allowed disabled:opacity-40"
        style={{
          minHeight: "96px",
          fontSize: 28,
          backgroundColor: "var(--color-lime)",
          borderRadius: "var(--radius-sharp)",
        }}
      >
        {t("workoutRunner.actions.done")}
      </button>
      <p className="sr-only" aria-live="polite">
        {t("common.currentLevel")}: {reps}
      </p>
      <p className="text-center font-sans text-sm text-[var(--color-ink)]">
        {t("workoutRunner.setOf", { current: completedSets + 1, total: totalSets })}
      </p>
      {keypadOpen && (
        <div
          data-testid="keypad-backdrop"
          className="fixed inset-0 z-40 bg-[var(--color-void)]/60"
          onClick={() => setKeypadOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="absolute inset-x-0 bottom-0"
          >
            <RepKeypad
              initialValue={reps}
              onChange={(v) => onRepsChange(v)}
              onCancel={() => setKeypadOpen(false)}
              onConfirm={(v) => {
                // The keypad only commits the rep number and
                // closes the sheet. It does NOT call `onDone` —
                // completing the set is the separate Set-done
                // button, which stays disabled until reps > 0.
                // The keypad ⌫/✓ live-preview pattern is the
                // point: the person sees the number land and
                // presses Set-done on purpose.
                onRepsChange(v);
                setKeypadOpen(false);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}